import { Duration, format, formatDistanceStrict, formatDuration, parseISO } from 'date-fns';
import { de, enGB, ja, ko, ru, uk, zhCN } from 'date-fns/locale';
import { LANGUAGES } from '@/state/member/memberTypes';

interface DurationHelpersOptions {
    long?: boolean;
    units?: string[];
    showJustBiggestUnit?: boolean;
    maxUnitsCount?: number;
    zero?: boolean;
}

const locales = {
    en: enGB,
    ru,
    uk,
    ko,
    ja,
    zh: zhCN,
    de,
};

/** Formats a date-fns Duration object to a localized string; supports short (e.g. "2d 3h") and long formats, unit filtering, and max unit count. */
export const durationFormatLocalized = (
    durationObj,
    locale: LANGUAGES | string,
    options?: DurationHelpersOptions,
) => {
    const defaultOptions: DurationHelpersOptions = {
        long: false,
        units: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
        showJustBiggestUnit: false,
        maxUnitsCount: undefined,
        zero: true,
    };
    const resultOptions: DurationHelpersOptions = { ...defaultOptions, ...options };

    const { long, units } = resultOptions;
    const shortFormat = {
        en: {
            years: 'y',
            months: 'M',
            days: 'd',
            hours: 'h',
            minutes: 'm',
            seconds: 's',
        },
        ru: {
            years: 'г',
            months: 'м',
            days: 'д',
            hours: 'ч',
            minutes: 'м',
            seconds: 'с',
        },
        uk: {
            years: 'р',
            months: 'м',
            days: 'д',
            hours: 'г',
            minutes: 'хв',
            seconds: 'с',
        },
        de: {
            years: 'J',
            months: 'M',
            days: 'T',
            hours: 'Std',
            minutes: 'Min',
            seconds: 'Sek',
        },
    };
    let filteredDuration;
    let visibleUnit;
    if (options?.showJustBiggestUnit) {
        visibleUnit = resultOptions.units?.find((unit) => durationObj[unit]);
        if (visibleUnit) filteredDuration = [[visibleUnit, durationObj[visibleUnit]]];
    }
    if (long) {
        return formatDuration(durationObj, {
            locale: locales[locale],
            format: visibleUnit ? [visibleUnit] : units,
            zero: options?.zero,
        });
    }

    if (options?.units) {
        if (options?.maxUnitsCount) {
            const biggestExistUnit = options?.units?.find((unit) => durationObj[unit]);
            if (biggestExistUnit) {
                const index = options?.units?.indexOf(biggestExistUnit);
                let filteredUnits;
                if (index || index === 0) {
                    filteredUnits = options?.units?.slice(index, index + options?.maxUnitsCount);
                }
                filteredDuration = Object.entries(durationObj).filter(([unit, _]) =>
                    filteredUnits?.includes(unit),
                );
            }
        } else {
            filteredDuration = Object.entries(durationObj).filter(([unit, _]) =>
                options?.units?.includes(unit),
            );
        }
    } else filteredDuration = Object.entries(durationObj).filter(([_, value]) => value);
    const localeShortFormat = shortFormat[locale] || shortFormat.en;

    if (!filteredDuration) return `< 1 ${localeShortFormat['seconds']}`;
    return filteredDuration.map(([unit, value]) => `${value}${localeShortFormat[unit]}`).join(' ');
};

/** Converts a seconds number to a date-fns Duration object broken into the specified time units. */
export const secondsToDuration = (
    seconds,
    units = ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
): Duration => {
    const duration: Duration = {};
    let parsedSeconds = Math.floor(seconds);
    if (units?.includes('years')) {
        duration.years = Math.floor(parsedSeconds / (365 * 24 * 60 * 60));
        parsedSeconds %= 365 * 24 * 60 * 60;
    }

    if (units?.includes('months')) {
        duration.months = Math.floor(parsedSeconds / (30 * 24 * 60 * 60));
        parsedSeconds %= 30 * 24 * 60 * 60;
    }

    if (units?.includes('days')) {
        duration.days = Math.floor(parsedSeconds / (24 * 60 * 60));
        parsedSeconds %= 24 * 60 * 60;
    }

    if (units?.includes('hours')) {
        duration.hours = Math.floor(parsedSeconds / (60 * 60));
        parsedSeconds %= 60 * 60;
    }

    if (units?.includes('minutes')) {
        duration.minutes = Math.floor(parsedSeconds / 60);
        parsedSeconds %= 60;
    }

    if (units?.includes('seconds')) {
        duration.seconds = parsedSeconds;
    }

    if (Object.values(duration).every((value) => value === 0)) {
        duration.seconds = +seconds.toFixed(2);
    }
    return duration;
};

/** Converts a seconds value directly to a localized formatted duration string (combines secondsToDuration + durationFormatLocalized). */
export const convertSecondsToDuration = (
    seconds,
    locale: LANGUAGES | string = LANGUAGES.EN,
    options?: DurationHelpersOptions,
) => {
    const defaultOptions: DurationHelpersOptions = {
        long: false,
        units: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
    };
    const resultOptions: DurationHelpersOptions = { ...defaultOptions, ...options };
    const { units } = resultOptions;
    const duration: Duration = secondsToDuration(seconds, units);
    return durationFormatLocalized(duration, locale, options);
};

/** Returns the absolute second difference between two date strings. */
export const differenceInSeconds = (dateString1, dateString2) => {
    try {
        const date1 = new Date(dateString1);
        const date2 = new Date(dateString2);

        const difference: number = date2.getTime() - date1.getTime();

        const differenceInMillis = Math.abs(difference);

        return Math.floor(differenceInMillis / 1000);
    } catch (error) {
        console.error(error);
    }
};
/** Calculates and formats the duration between two dates with locale and unit options; defaults the second date to now. */
export const calculateDurationBetweenDates = (
    date1,
    date2: string | Date = new Date(),
    options,
): string => {
    const defaultOptions = {
        long: false,
        locale: 'en',
        units: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
        showJustBiggestUnit: false,
    };
    const resultOptions = {
        ...defaultOptions,
        ...options,
    };
    const parsedDate1 = typeof date1 === 'string' ? parseISO(date1) : date1;
    let parsedDate2 = typeof date2 === 'string' ? parseISO(date2) : date2;

    if (options.utc) {
        parsedDate2 = new Date(parsedDate2.toISOString());
    }
    const [start, end] =
        parsedDate1 <= parsedDate2 ? [parsedDate1, parsedDate2] : [parsedDate2, parsedDate1];
    const filteredDuration = secondsToDuration(
        differenceInSeconds(start, end),
        resultOptions.units,
    );
    return durationFormatLocalized(filteredDuration, resultOptions.locale, options);
};

/** Parses a "MM SS" duration string (e.g. "5 30") into total seconds. */
export const parseStringDurationShort = (durationString: string): number => {
    const splitString = durationString.split(' ');
    const minutes = parseInt(splitString[0]);
    const seconds = parseInt(splitString[1]);
    return minutes * 60 + seconds;
};

export const formatBucketTimeAgo = (timestamp: string, locale = 'en'): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const dateFnsLocale = locales[locale] || locales.en;
    const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    const timeAgo = formatDistanceStrict(date, now, { addSuffix: true, locale: dateFnsLocale });

    if (diffHours < 24) {
        return timeAgo;
    }

    const dateStr = format(date, 'd MMM', { locale: dateFnsLocale });
    return `${dateStr} (${timeAgo})`;
};

const TIME_OF_DEATH_UNITS: Record<
    string,
    { d: string; h: string; m: string; s: string; ago: string }
> = {
    en: { d: 'd', h: 'h', m: 'm', s: 's', ago: 'ago' },
    ru: { d: 'д', h: 'ч', m: 'м', s: 'с', ago: 'назад' },
    uk: { d: 'д', h: 'г', m: 'хв', s: 'с', ago: 'тому' },
    ko: { d: '일', h: '시간', m: '분', s: '초', ago: '전' },
    ja: { d: '日', h: '時間', m: '分', s: '秒', ago: '前' },
    zh: { d: '天', h: '小时', m: '分', s: '秒', ago: '前' },
    de: { d: 'T', h: 'Std', m: 'Min', s: 'Sek', ago: 'her' },
};

interface TimeOfDeathParts {
    value: string;
    suffix: string;
    suffixBefore: boolean;
}

const getLargestTimeOfDeathUnit = (
    totalSeconds: number,
    u: (typeof TIME_OF_DEATH_UNITS)[string],
): string => {
    if (totalSeconds >= 86400) return `${Math.floor(totalSeconds / 86400)}${u.d}`;
    if (totalSeconds >= 3600) return `${Math.floor(totalSeconds / 3600)}${u.h}`;
    if (totalSeconds >= 60) return `${Math.floor(totalSeconds / 60)}${u.m}`;
    return `${totalSeconds}${u.s}`;
};

export const formatTimeOfDeath = <ReturnParts extends boolean = false>(
    timestamp: string,
    locale = 'en',
    justNowLabel = 'Just now',
    returnParts = false as ReturnParts,
    singleUnit = false,
): ReturnParts extends true ? TimeOfDeathParts : string => {
    const diffMs = Math.max(0, Date.now() - new Date(timestamp).getTime());
    const totalSeconds = Math.floor(diffMs / 1000);

    const u = TIME_OF_DEATH_UNITS[locale] ?? TIME_OF_DEATH_UNITS.en;

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Languages where the suffix comes before (ko, ja, zh) vs after (en, ru, uk)
    const suffixBefore = ['ko', 'ja', 'zh'].includes(locale);
    const wrap = (value: string) => (suffixBefore ? `${value}${u.ago}` : `${value} ${u.ago}`);

    const twoUnitValue =
        days > 0
            ? `${days}${u.d} ${hours}${u.h}`
            : hours > 0
              ? `${hours}${u.h} ${minutes}${u.m}`
              : minutes > 0
                ? `${minutes}${u.m} ${seconds}${u.s}`
                : `${seconds}${u.s}`;
    const value = singleUnit ? getLargestTimeOfDeathUnit(totalSeconds, u) : twoUnitValue;

    if (returnParts) {
        return { value, suffix: u.ago, suffixBefore } as ReturnParts extends true
            ? TimeOfDeathParts
            : string;
    }
    return (totalSeconds < 10 ? justNowLabel : wrap(value)) as ReturnParts extends true
        ? TimeOfDeathParts
        : string;
};

export const defineTimeRange = (openTime: string): string => {
    const milliseconds = new Date().getTime() - new Date(openTime).getTime();
    const seconds = milliseconds / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;

    if (seconds < 60) {
        return 'seconds';
    }
    if (minutes < 60) {
        return 'minutes';
    }
    if (hours < 24) {
        return 'hours';
    }
    return 'days';
};

/** Formats a date as a compact elapsed string (e.g. "3d", "14h", "45m"); returns "—" for invalid or future dates. */
export const formatAge = (date: string): string => {
    if (!date) return '—';
    const diffMs = Date.now() - new Date(date).getTime();
    if (diffMs < 0) return '—';
    const days = Math.floor(diffMs / 86400000);
    if (days >= 1) return `${days}d`;
    const hours = Math.floor(diffMs / 3600000);
    if (hours >= 1) return `${hours}h`;
    return `${Math.floor(diffMs / 60000)}m`;
};
